import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import services from '../services';
import withResponseHandling from './withResponseHandling';
import withMessage from './withMessage';
import { acCarteirasChanged } from '../store/actions';

const PAGE_SIZE = 25;

const withAvaliacoes = Component => {
  function WrapComponent(props) {
    const entidadeId = useSelector(state => state.entidade.current.default_entidade_id);
    const storeDispatch = useDispatch();

    const [avaliacoes, setAvaliacoes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [rowSelection, setRowSelection] = useState({});
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: PAGE_SIZE
    });

    function updateStore(pData) {
      storeDispatch(acCarteirasChanged(pData));
    }

    const readAvaliacoes = useCallback(
      async (pParams = {}, pCallback) => {
        if (!avaliacoes.length) {
          setIsLoading(true);
        } else {
          setIsRefetching(true);
        }

        const xParams = {
          size: PAGE_SIZE,
          page: 1,
          sort: 'favorito desc, carteira',
          ...pParams
        };

        try {
          const rRes = await services.mockAvaliacoes.avaliacoesRepo.list({
            page: xParams.page,
            size: xParams.size
          });

          if (!rRes.ok) {
            props.responseErrorHandling(rRes.error);
            return;
          }

          const xAllItems = Array.isArray(rRes.data) ? rRes.data : [];
          const xPage = Number(xParams.page) > 0 ? Number(xParams.page) : 1;
          const xSize = Number(xParams.size) > 0 ? Number(xParams.size) : PAGE_SIZE;
          const xStart = (xPage - 1) * xSize;
          const xItems = xAllItems.slice(xStart, xStart + xSize);

          setAvaliacoes([
            ...xItems,
            ...[
              {
                avaliacao_id: 1,
                gestor: 'Gestor Alpha',
                fundo: 'Fundo Alpha A',
                status: 'Aguardando respostas',
                score: null,
                modified: new Date().toLocaleDateString(),
                reenvio: true
              },
              {
                avaliacao_id: 1,
                gestor: 'Gestor Alpha',
                fundo: null,
                status: 'Concluído pelo Gestor',
                score: null,
                modified: new Date().toLocaleDateString(),
                reenvio: false
              },
              {
                avaliacao_id: 2,
                gestor: 'Gestor Beta',
                fundo: 'Fundo Beta',
                status: 'Concluído',
                score: 2.6,
                modified: new Date().toLocaleDateString(),
                reenvio: false
              },
              {
                avaliacao_id: 3,
                gestor: 'Gestor Gamma',
                fundo: 'Fundo Gamma',
                status: 'Rascunho',
                score: null,
                modified: new Date().toLocaleDateString(),
                reenvio: false
              }
            ]
          ]);
          setRowCount(xAllItems.length);
          pCallback && pCallback(xItems);
        } catch (error) {
          props.responseErrorHandling(error);
        } finally {
          setIsLoading(false);
          setIsRefetching(false);
        }
      },
      [avaliacoes.length, props]
    );

    const deleteAvaliacoes = async (pAvaliacaoId, onFinish) => {
      try {
        const rRes = await services.mockAvaliacoes.avaliacoesRepo.remove(pAvaliacaoId);

        if (!rRes.ok) {
          props.responseErrorHandling(rRes.error);
          return;
        }

        props.onMessageSuccess('Avaliação excluída com sucesso');
        await readAvaliacoes(
          {
            page: pagination.pageIndex + 1,
            size: pagination.pageSize
          },
          updateStore
        );
        setRowSelection({});
      } catch (error) {
        props.responseErrorHandling(error);
      } finally {
        onFinish && onFinish();
      }
    };

    const bulkDeleteAvaliacoes = async pRows => {
      const xAvaliacoesId = pRows.map(xRow => {
        const { avaliacao_id } = xRow.original;
        return avaliacao_id;
      });

      try {
        const xResults = await Promise.all(
          xAvaliacoesId.map(xAvaliacaoId =>
            services.mockAvaliacoes.avaliacoesRepo.remove(xAvaliacaoId)
          )
        );
        const xErrorResult = xResults.find(xResult => !xResult.ok);

        if (xErrorResult) {
          props.responseErrorHandling(xErrorResult.error);
          return;
        }

        props.onMessageSuccess('Avaliações excluídas com sucesso');
        await readAvaliacoes(
          {
            page: pagination.pageIndex + 1,
            size: pagination.pageSize
          },
          updateStore
        );
        setRowSelection({});
      } catch (error) {
        props.responseErrorHandling(error);
      }
    };

    useEffect(() => {
      const xPage = pagination.pageIndex + 1;

      readAvaliacoes({
        page: xPage,
        size: pagination.pageSize
      });
    }, [readAvaliacoes, pagination.pageIndex, pagination.pageSize, entidadeId]);

    const xProps = {
      avaliacoes,
      isLoading,
      isRefetching,
      rowCount,
      rowSelection,
      pagination,
      setPagination,
      setRowSelection,
      readAvaliacoes,
      deleteAvaliacoes,
      bulkDeleteAvaliacoes
    };

    return <Component {...xProps} />;
  }

  return withMessage(withResponseHandling(WrapComponent));
};

export default withAvaliacoes;
